/**
 * SEPARATE guarantee (S) — Double category structure.
 *
 * Neutralizes epistemic lift (G2) by enforcing disjoint data and instruction
 * domains. Data cannot be executed as code; code cannot be reinterpreted as data.
 *
 * Agda source: Kernel/SEPARATE/Neutralization.agda
 * Realization fidelity: FULL (branded types enforce domain separation)
 * Safety class: Compositional (innate immunity)
 * Neutralization mode: IMMUNE — violations have no type representation
 * Tier: 2 (structural-geometric)
 * Legacy name: BOUND
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { Brand, Guarantee } from "../foundation/types.js";
import { SEPARATE_META } from "../shared/theorem-meta.js";
export { SEPARATE_META };

// ── Structure: Double category ─────────────────────────────────

/** A value in the data domain — cannot be executed. */
export type DataValue<T> = Brand<T, "DataValue">;

/** A value in the instruction domain — cannot be reinterpreted as data. */
export type Instruction<T> = Brand<T, "Instruction">;

/** Mark a value as data (input validation boundary). */
export function asData<T>(value: T): DataValue<T> {
  return value as DataValue<T>;
}

/** Mark a value as an instruction (trusted code boundary). */
export function asInstruction<T>(value: T): Instruction<T> {
  return value as Instruction<T>;
}

/** A violation of separation — data executing as code or vice versa. */
export interface SeparateViolation {
  readonly __brand: "SeparateViolation";
  readonly kind: "domain-crossing";
}

/**
 * A SEPARATE-compliant system: data and instruction domains are disjoint.
 * The double category structure ensures horizontal morphisms (data transforms)
 * and vertical morphisms (instruction sequencing) cannot be interchanged.
 */
export interface SeparateStructure<D, I> {
  readonly dataOps: {
    readonly validate: (input: unknown) => DataValue<D>;
    readonly transform: (data: DataValue<D>) => DataValue<D>;
  };
  readonly instructionOps: {
    readonly compile: (source: string) => Instruction<I>;
    readonly execute: (instr: Instruction<I>) => unknown;
  };
}

export function createSeparateGuarantee<D, I>(
  structure: SeparateStructure<D, I>,
): Guarantee<SeparateStructure<D, I>, SeparateViolation> {
  return {
    structure,
    neutralize: (_violation: SeparateViolation): never => {
      throw new Error(
        "SEPARATE violation is structurally inexpressible: " +
          "branded types prevent domain crossing",
      );
    },
    meta: SEPARATE_META,
  };
}
